import React, { useState } from 'react';
import { DownloadIcon, LinkedinIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { profile } from '../data/profile';
import { SectionHeading } from '../components/SectionHeading';
import { IndustryBackdrop } from '../components/IndustryBackdrop';
type Status = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState({
    name: '',
    email: '',
    organisation: '',
    message: ''
  });
  const update = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(current => ({
    ...current,
    [field]: event.target.value
  }));
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('success');
      setForm({
        name: '',
        email: '',
        organisation: '',
        message: ''
      });
    }, 800);
  };
  const fieldClass = 'w-full border border-ink/20 bg-white px-4 py-3 text-lg text-ink placeholder:text-slate-400 transition-colors duration-150 ease-exec focus:border-grass-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grass-200';
  return <section className="relative min-h-[70vh] overflow-hidden bg-grass-50 pt-32 pb-24 lg:pb-32" data-id="element-95" data-mp="1j8y09c08slqn">
      <IndustryBackdrop data-id="element-96" data-mp="minolya4ke7v" />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10" data-id="element-97" data-mp="qi65miyts55f">
        <SectionHeading eyebrow="CONTACT" title="Start a conversation" intro="Direct contact details and a private inquiry form for executive sourcing mandates, advisory engagements, and speaking requests." data-id="element-98" data-mp="qk8wf57sy2b7" />

        <div className="mt-14 grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-99" data-mp="26wxtqjoxuq8x">
          <div data-id="element-100" data-mp="2bv8zai87aelc">
            <dl className="space-y-8" data-id="element-101" data-mp="2buptfzvtrgno">
              <div className="flex items-start gap-4 border-b border-ink/10 pb-6" data-id="element-102" data-mp="2bto5iutpmzoj">
                <PhoneIcon className="mt-1 h-5 w-5 flex-none text-gold-500" data-id="element-103" data-mp="1m626d9lkpzmo" />
                <div data-id="element-104" data-mp="1h920ayoq4wvk">
                  <dt className="text-sm tracking-wider2 text-grass-600" data-id="element-105" data-mp="1h7ha1l9g003i">PHONE NUMBER</dt>
                  <dd className="mt-1 text-xl text-ink" data-id="element-106" data-mp="1w489fuj49bt4">
                    <a className="transition-colors duration-150 ease-exec hover:text-grass-700" href="tel:+16178957529" data-id="element-107" data-mp="1r6gfyw2tphmt">
                      {profile.phone}
                    </a>
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-4 border-b border-ink/10 pb-6" data-id="element-108" data-mp="1d773a6dtt5nl">
                <MailIcon className="mt-1 h-5 w-5 flex-none text-gold-500" data-id="element-109" data-mp="18fytvachxkaf" />
                <div data-id="element-110" data-mp="18dvuhzpgeet1">
                  <dt className="text-sm tracking-wider2 text-grass-600" data-id="element-111" data-mp="kqe52atnvst8">GMAIL</dt>
                  <dd className="mt-1 break-all text-xl text-ink" data-id="element-112" data-mp="ko1gheteek77">
                    <a className="transition-colors duration-150 ease-exec hover:text-grass-700" href={`mailto:${profile.email}`} data-id="element-113" data-mp="fqruk957wqo4">
                      {profile.email}
                    </a>
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-4 border-b border-ink/10 pb-6" data-id="element-114" data-mp="2hedyyf4d3z8c">
                <MapPinIcon className="mt-1 h-5 w-5 flex-none text-gold-500" data-id="element-115" data-mp="2mg3di4qsmv16" />
                <div data-id="element-116" data-mp="3a65xdxkk0uop">
                  <dt className="text-sm tracking-wider2 text-grass-600" data-id="element-117" data-mp="3a5xla7w99tek">ADDRESS</dt>
                  <dd className="mt-1 text-xl text-ink" data-id="element-118" data-mp="3f2wcahkmhk8j">{profile.location}</dd>
                </div>
              </div>

              <div className="flex items-start gap-4" data-id="element-119" data-mp="316ix5f3k9ux8">
                <LinkedinIcon className="mt-1 h-5 w-5 flex-none text-gold-500" data-id="element-120" data-mp="3559wztzevqo1" />
                <div data-id="element-121" data-mp="1ppj94vo8y290">
                  <dt className="text-sm tracking-wider2 text-grass-600" data-id="element-122" data-mp="1po82mo3f2ge7">LINKEDIN</dt>
                  <dd className="mt-1 text-xl text-ink" data-id="element-123" data-mp="1tr4r2vfn16q2">
                    <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="transition-colors duration-150 ease-exec hover:text-grass-700" data-id="element-124" data-mp="1tos23ybgpw3i">
                      {profile.linkedin}
                    </a>
                  </dd>
                </div>
              </div>
            </dl>

            <a href={profile.linkedinUrl} target="_blank" rel="noreferrer" className="mt-10 inline-flex items-center gap-3 bg-ink px-7 py-4 text-lg text-white transition-colors duration-150 ease-exec hover:bg-ink-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2" data-id="element-125" data-mp="2swvarfhy6e5u">
              <DownloadIcon className="h-5 w-5 text-gold-300" data-id="element-126" data-mp="2ez6pi6jyx509" />
              Download Executive Resume
            </a>
          </div>

          <form onSubmit={onSubmit} className="bg-white p-8 ring-1 ring-ink/10 lg:p-10" noValidate data-id="element-127" data-mp="13hfg154rsuzh">
            <h3 className="text-2xl text-ink" data-id="element-128" data-mp="3f5tzq8zamjep">Send an inquiry</h3>
            <p className="mt-2 text-base text-slate-600" data-id="element-129" data-mp="3f3h8f57nawoz">
              Responses are typically returned within two business days.
            </p>

            <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-130" data-mp="3o4xl5jlxnawd">
              <div data-id="element-131" data-mp="3swp2r4n426ad">
                <label htmlFor="name" className="block text-base text-ink" data-id="element-132" data-mp="3sx8mmqwbn4r1">
                  Full name
                </label>
                <input id="name" name="name" value={form.name} onChange={update('name')} className={`mt-2 ${fieldClass}`} placeholder="Your name" required data-id="element-133" data-mp="1pmb6c3d1xzm" />
              </div>
              <div data-id="element-134" data-mp="fpdtuq4to2jy">
                <label htmlFor="email" className="block text-base text-ink" data-id="element-135" data-mp="fnszp1mf6lmm">
                  Email address
                </label>
                <input id="email" name="email" type="email" value={form.email} onChange={update('email')} className={`mt-2 ${fieldClass}`} placeholder="name@company.com" required data-id="element-136" data-mp="2hgqmafplshz4" />
              </div>
            </div>

            <div className="mt-6" data-id="element-137" data-mp="3ou177edab1wx">
              <label htmlFor="organisation" className="block text-base text-ink" data-id="element-138" data-mp="3oszpir1rjh68">
                Organisation
              </label>
              <input id="organisation" name="organisation" value={form.organisation} onChange={update('organisation')} className={`mt-2 ${fieldClass}`} placeholder="Company or fund" data-id="element-139" data-mp="7jt9usiry7un" />
            </div>

            <div className="mt-6" data-id="element-140" data-mp="cf083kt9ykc6">
              <label htmlFor="message" className="block text-base text-ink" data-id="element-141" data-mp="qbm38o1r9v2w">
                How can Christine help
              </label>
              <textarea id="message" name="message" rows={5} value={form.message} onChange={update('message')} className={`mt-2 ${fieldClass}`} placeholder="Share the mandate, timeline, and scope" required data-id="element-142" data-mp="levllwaayrfd" />
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-5" data-id="element-143" data-mp="zz83jm2pkw98">
              <button type="submit" disabled={status === 'submitting'} className="bg-grass-600 px-8 py-4 text-lg text-white transition-colors duration-150 ease-exec hover:bg-grass-500 disabled:cursor-not-allowed disabled:bg-grass-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2" data-id="element-144" data-mp="m8miprtp2c8p">
                {status === 'submitting' ? 'Sending inquiry' : 'Submit inquiry'}
              </button>

              <p aria-live="polite" className="text-base" data-id="element-145" data-mp="7b3z13vvip84">
                {status === 'success' ? <span className="text-grass-700" data-id="element-146" data-mp="nql5lj0uykhf">
                    Thank you. Your inquiry has been received.
                  </span> : null}
                {status === 'error' ? <span className="text-red-700" data-id="element-147" data-mp="ds5q6db70znr">
                    Please complete your name, email, and message.
                  </span> : null}
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>;
}